import type { RaceNight, RigClass } from '../models/raceNights/RaceNight';
import type { PaymentOptionDto, SeatPaymentKind } from '../models/sessions/Session';

export const WEBSITE_MEMBERSHIPS = 'https://www.worldofracing.com/memberships';

const DOW = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MON = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const title = (s: string) => s.charAt(0) + s.slice(1).toLowerCase();

/** "YYYY-MM-DD" as a LOCAL date: new Date("2026-09-29") would be UTC midnight, a day early in PT. */
const parseYmd = (ymd: string): Date => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** The date block on a row: "TUE" / 29 / "SEP". */
export const dateParts = (ymd: string) => {
  const d = parseYmd(ymd);
  return { dow: DOW[d.getDay()], day: d.getDate(), month: MON[d.getMonth()] };
};

/** "Tue 29 Sep". */
export const shortDate = (ymd: string): string => {
  const p = dateParts(ymd);
  return `${title(p.dow)} ${p.day} ${title(p.month)}`;
};

const clock = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return { text: `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')}`, meridiem: h < 12 ? 'AM' : 'PM' };
};

/** "7:00 – 9:00 PM", or "11:30 AM – 1:00 PM" when the window crosses noon. */
export const windowLabel = (start: string, end: string): string => {
  const a = clock(start);
  const b = clock(end);
  return a.meridiem === b.meridiem
    ? `${a.text} – ${b.text} ${b.meridiem}`
    : `${a.text} ${a.meridiem} – ${b.text} ${b.meridiem}`;
};

export type NightGroup = 'This week' | 'Next week' | 'Later';

export const groupOf = (ymd: string, today: Date = new Date()): NightGroup => {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((parseYmd(ymd).getTime() - start.getTime()) / 86_400_000);
  return days < 7 ? 'This week' : days < 14 ? 'Next week' : 'Later';
};

/** Everyone on a class fits in what that class has left. */
export const rigsFit = (night: RaceNight, rigs: RigClass[]): boolean =>
  rigs.filter((r) => r === 'pro').length <= night.seats.proLeft
  && rigs.filter((r) => r === 'esports').length <= night.seats.esportsLeft;

/** Whether seat `seatIndex` (0 = the member) may switch to `rig` without overfilling a class. */
export const canSeatUseNight = (night: RaceNight, rigs: RigClass[], seatIndex: number, rig: RigClass): boolean =>
  rigsFit(night, rigs.map((r, i) => (i === seatIndex ? rig : r)));

export const ordinal = (n: number): string => {
  const teen = n % 100 >= 11 && n % 100 <= 13;
  const suffix = teen ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th';
  return `${n}${suffix}`;
};

export const money = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

/** A row's price: whole dollars when there are no cents ("$49"), else "$47.20". */
export const priceLabel = (cents: number): string => (cents % 100 === 0 ? `$${cents / 100}` : money(cents));

/** The rig classes the night has rigs for. 0 rigs means the class isn't offered (A8), not "sold out". */
export const rigsOffered = (night: Pick<RaceNight, 'seats'>): RigClass[] =>
  (['pro', 'esports'] as RigClass[]).filter((r) => (r === 'pro' ? night.seats.proSeats : night.seats.esportsSeats) > 0);

/** The rig a claim starts on: Pro when it has a seat left, else Esports; always a class the night has. */
export const defaultRig = (night: Pick<RaceNight, 'seats'>): RigClass => {
  const offered = rigsOffered(night);
  const withRoom = offered.find((r) => (r === 'pro' ? night.seats.proLeft : night.seats.esportsLeft) > 0);
  return withRoom ?? offered[0] ?? 'pro';
};

/** How a night's pill is coloured: the member's own seat first, then the night's treatment. */
export type NightPillTone = 'in' | 'wait' | 'lock' | 'price' | 'credits' | 'included';

/**
 * What a night is to THIS member, as one pill: the Race nights page and the home both read it here so
 * they never disagree. The member's own seat (`mine`) comes first; a night they hold included counts
 * toward their limit, so its options may read credits. Never keyed off creditCost > 0: it is non-zero
 * on Included and card nights too.
 */
export const nightPill = (night: RaceNight): { label: string; tone: NightPillTone } => {
  if (night.mine === 'seated') return { label: "You're in", tone: 'in' };
  if (night.mine === 'paymentPending') return { label: 'Payment pending', tone: 'wait' };
  if (night.mine === 'waitlisted') return { label: `Waitlist · W${night.waitlistPosition}`, tone: 'wait' };
  switch (night.treatment) {
    case 'locked':
      return { label: night.lockedLabel ?? 'Not with your tier', tone: 'lock' };
    case 'card': {
      // The card price of the rig a claim starts on, never the options' Pro-only price for an Esports night.
      const rig = rigsOffered(night)[0] ?? 'pro';
      return { label: priceLabel(night.cardPriceCents?.[rig] ?? night.ticketPriceCents[rig]), tone: 'price' };
    }
    case 'credits': {
      // The credits option's own cost: creditCost is non-zero on Included nights too.
      const credits = night.options.find((o) => o.payment === 'credits')?.credits ?? night.creditCost;
      return { label: `${credits} credits`, tone: 'credits' };
    }
    default:
      return { label: 'Included', tone: 'included' };
  }
};

/** A night the member can act on: one they hold a place on, or any treatment but locked. */
export const isActionableNight = (night: Pick<RaceNight, 'mine' | 'treatment'>): boolean =>
  night.mine !== 'none' || night.treatment !== 'locked';

type Limit = RaceNight['includedLimit'];

/** Spec §6: the includedLimit reason (on a greyed Included option, or the 422 refusal), in words. */
export const includedLimitMessage = (max: number | null | undefined, nextFreesOn: string | null | undefined): string => {
  const holding = typeof max === 'number'
    ? `You're holding ${max} included nights.`
    : "You're holding your limit of included nights.";
  return nextFreesOn
    ? `${holding} Claim this one with credits or card, or included after ${shortDate(nextFreesOn)}.`
    : `${holding} Claim this one with credits or card.`;
};

/** Spec §1.3: the line the claim panel adds at the limit. */
export const includedLimitLine = (limit: NonNullable<Limit>): string => (limit.nextFreesOn
  ? `You're holding ${limit.max} included nights; the next one is ${shortDate(limit.nextFreesOn)}. `
    + 'Claim this one with credits or card, or included after that night.'
  : includedLimitMessage(limit.max, null));

/** An option's reason as the member reads it: the server's codes mapped to words, anything else as sent. */
export const optionReason = (reason: string | null, limit: Limit): string | null =>
  (reason === 'includedLimit' ? includedLimitMessage(limit?.max, limit?.nextFreesOn) : reason);

/** The member's own Included option is greyed because of the limit (spec D3). */
export const atIncludedLimit = (night: Pick<RaceNight, 'options'>): boolean =>
  night.options.some((o) => o.payment === 'included' && !o.available && o.reason === 'includedLimit');

/**
 * How a seat is paid: the choice while the night still offers it, else the first available option in the
 * server's order (cheapest first), else null when nothing is available.
 */
export const effectivePayment = <K extends SeatPaymentKind>(
  options: PaymentOptionDto[] | null | undefined, chosen: K | null | undefined,
): K | null => {
  const available = (options ?? []).filter((o) => o.available);
  if (chosen && available.some((o) => o.payment === chosen)) return chosen;
  return (available[0]?.payment as K | undefined) ?? null;
};

const creditsOf = (options: PaymentOptionDto[] | null | undefined): number =>
  options?.find((o) => o.payment === 'credits')?.credits ?? 0;

/**
 * What the chosen seats come to, as a QUOTE: card seats at the rig actually chosen (never the options'
 * Pro-only priceCents), credit seats at their option's credits. Before tax; the charge is the 202's amountMinor.
 */
export const claimQuote = (
  night: Pick<RaceNight, 'options' | 'cardPriceCents' | 'guestOptions' | 'guestCardPriceCents'>,
  rig: RigClass,
  memberPayment: SeatPaymentKind | null,
  guests: { rigClass: RigClass; payment: SeatPaymentKind | null }[],
): { cardCents: number; credits: number } => {
  let cardCents = memberPayment === 'card' ? night.cardPriceCents?.[rig] ?? 0 : 0;
  let credits = memberPayment === 'credits' ? creditsOf(night.options) : 0;
  guests.forEach((g) => {
    if (g.payment === 'card') cardCents += night.guestCardPriceCents?.[g.rigClass] ?? 0;
    if (g.payment === 'credits') credits += creditsOf(night.guestOptions);
  });
  return { cardCents, credits };
};
